import { z } from 'zod';
export const PROTOCOL_VERSION = 1;
export const inviteCapacityClassSchema = z.enum(['demo-5']);
export const roomSlugSchema = z
    .string()
    .min(1)
    .max(64)
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/);
export const roomTypeSchema = z.enum(['instant', 'recurring']);
export const roomVisibilitySchema = z.enum(['public', 'private']);
export const roomRoleSchema = z.enum(['owner', 'admin', 'member']);
export const whoCanSpeakSchema = z.enum(['everyone', 'owner_and_admins', 'owner_only']);
export const roomTitleSchema = z.string().trim().min(1).max(80);
export const privateAccessCodeSchema = z
    .string()
    .trim()
    .transform((value) => value.toUpperCase())
    .pipe(z
    .string()
    .length(6)
    .regex(/^[A-Z]{6}$/));
export const inviteTokenClaimsSchema = z.strictObject({
    version: z.literal(PROTOCOL_VERSION),
    kind: z.literal('room-invite'),
    inviteId: z.uuid(),
    roomSlug: roomSlugSchema,
    expiresAt: z.int().positive(),
    capacityClass: inviteCapacityClassSchema,
    /** Present for registry-backed rooms. Legacy facilitated invites omit it during migration. */
    roomGeneration: z.int().nonnegative().optional(),
});
export const inviteJoinRequestSchema = z.strictObject({
    version: z.literal(PROTOCOL_VERSION),
    slug: roomSlugSchema,
    token: z.string().min(1).max(4_096),
    browserCredential: z.string().min(1).max(4_096).optional(),
    roomCapability: z.string().min(1).max(4_096).optional(),
});
export const inviteJoinErrorCodeSchema = z.enum([
    'invalid-request',
    'invalid-signature',
    'invalid-claims',
    'expired',
    'revoked',
    'unknown-slug',
    'room-full',
    'room-locked',
    'approval-required',
    'browser-blocked',
    'rate-limited',
    'internal-error',
]);
export const colyseusSeatReservationSchema = z.strictObject({
    name: z.string().min(1),
    sessionId: z.string().min(1),
    roomId: z.string().min(1),
    publicAddress: z.string().min(1).optional(),
    processId: z.string().min(1).optional(),
    reconnectionToken: z.string().min(1).optional(),
    devMode: z.boolean().optional(),
});
export const inviteJoinSuccessSchema = z.strictObject({
    ok: z.literal(true),
    reservation: colyseusSeatReservationSchema,
    browserCredential: z.string().min(1).max(4_096).optional(),
});
export const inviteJoinErrorSchema = z.strictObject({
    ok: z.literal(false),
    error: z.strictObject({
        code: inviteJoinErrorCodeSchema,
        retryAfterSeconds: z.int().positive().optional(),
    }),
});
export const inviteJoinResponseSchema = z.discriminatedUnion('ok', [
    inviteJoinSuccessSchema,
    inviteJoinErrorSchema,
]);
/**
 * Self-service room creation.
 *
 * The request carries no slug. The server chooses it, so a caller cannot squat
 * a name, cannot probe whether a name is already taken, and cannot pick one
 * that is easy to guess. The response carries admission plus browser-bound
 * owner authority. Bearer credentials never belong in the room directory or
 * ordinary share links.
 */
export const createRoomRequestSchema = z.strictObject({
    version: z.literal(PROTOCOL_VERSION),
    title: roomTitleSchema,
    roomType: roomTypeSchema,
    visibility: roomVisibilitySchema,
});
export const createRoomErrorCodeSchema = z.enum([
    'invalid-request',
    /** Creation is not enabled in this environment. */
    'disabled',
    'rate-limited',
    /** The server is already holding as many concurrent rooms as it will allow. */
    'capacity-reached',
    'internal-error',
]);
export const createRoomSuccessSchema = z.strictObject({
    ok: z.literal(true),
    slug: roomSlugSchema,
    title: roomTitleSchema,
    roomType: roomTypeSchema,
    visibility: roomVisibilitySchema,
    token: z.string().min(1),
    browserCredential: z.string().min(1),
    ownerCapability: z.string().min(1),
    privateAccessCode: privateAccessCodeSchema.optional(),
});
export const createRoomErrorSchema = z.strictObject({
    ok: z.literal(false),
    error: z.strictObject({
        code: createRoomErrorCodeSchema,
        retryAfterSeconds: z.int().positive().optional(),
    }),
});
export const createRoomResponseSchema = z.discriminatedUnion('ok', [
    createRoomSuccessSchema,
    createRoomErrorSchema,
]);
export const roomDirectoryEntrySchema = z.strictObject({
    slug: roomSlugSchema,
    title: roomTitleSchema,
    roomType: roomTypeSchema,
    visibility: roomVisibilitySchema,
    occupancy: z.int().min(0).max(5),
    capacity: z.literal(5),
    approvalRequired: z.boolean(),
});
export const roomDirectoryResponseSchema = z.strictObject({
    version: z.literal(PROTOCOL_VERSION),
    rooms: z.array(roomDirectoryEntrySchema),
});
export const roomAdmissionRequestSchema = z.strictObject({
    version: z.literal(PROTOCOL_VERSION),
    browserCredential: z.string().min(1).max(4_096).optional(),
    roomCapability: z.string().min(1).max(4_096).optional(),
});
export const privateCodeExchangeRequestSchema = z.strictObject({
    version: z.literal(PROTOCOL_VERSION),
    slug: roomSlugSchema,
    code: privateAccessCodeSchema,
    browserCredential: z.string().min(1).max(4_096).optional(),
    roomCapability: z.string().min(1).max(4_096).optional(),
});
export const roomAdmissionErrorCodeSchema = z.enum([
    'invalid-request',
    'unknown-room',
    'room-closed',
    'wrong-visibility',
    'invalid-code',
    'room-locked',
    'approval-required',
    'browser-blocked',
    'room-full',
    'rate-limited',
    'internal-error',
]);
export const roomAdmissionResponseSchema = z.discriminatedUnion('ok', [
    z.strictObject({
        ok: z.literal(true),
        slug: roomSlugSchema,
        token: z.string().min(1),
        browserCredential: z.string().min(1),
    }),
    z.strictObject({
        ok: z.literal(false),
        error: z.strictObject({
            code: roomAdmissionErrorCodeSchema,
            retryAfterSeconds: z.int().positive().optional(),
            browserCredential: z.string().min(1).max(4_096).optional(),
        }),
    }),
]);
export const directionSchema = z.enum(['up', 'down', 'left', 'right']);
export const movementInputSchema = z.strictObject({
    version: z.literal(PROTOCOL_VERSION),
    sequence: z.int().nonnegative(),
    xPx: z.number(),
    yPx: z.number(),
    direction: directionSchema,
    isMoving: z.boolean(),
});
export const CHAT_BODY_MAX_LENGTH = 500;
/**
 * `PRD.md:390-394` fixes twenty: enough context for a five-minute facilitated
 * demo, small enough that a late joiner's first state payload stays trivial.
 */
export const CHAT_HISTORY_LIMIT = 20;
/**
 * Rejected rather than stripped, so this predicate stays a pure yes/no that the
 * browser can apply to its own input box and the server can apply again to the
 * wire, with no risk of the two disagreeing about what a "cleaned" string is.
 *
 * Covered: C0 controls except the line feed that `PHASE_2_PLAN.md:197-200` calls
 * an intended line break, DEL and the C1 block, the bidirectional override and
 * isolate characters — those reorder rendered glyphs, so a message can read as
 * something other than the code points it contains — and unpaired surrogates,
 * which are not Unicode scalar values and are silently rewritten to U+FFFD by
 * any UTF-8 encoder on the way to the wire. Ordinary Unicode, including emoji
 * and every script, is untouched.
 *
 * Caveat worth stating plainly: Zod runs `.trim()` before this refinement, so a
 * disallowed character at the very start or end that is also whitespace (a
 * trailing carriage return, say) is removed rather than rejected. That is the
 * desirable outcome for pasted text, but it does mean "reject, never strip" is
 * true of the interior of a message, not of its edges.
 */
const LINE_FEED_CODE_POINT = 0x00_0a;
/**
 * Characters that occupy no visual space but are neither whitespace nor format
 * characters, so neither `\p{White_Space}` nor `\p{Cf}` catches them. Without
 * these a body of only Hangul fillers renders as a blank message.
 */
const BLANK_BUT_NOT_WHITESPACE_CODE_POINTS = new Set([
    0x11_5f, // HANGUL CHOSEONG FILLER
    0x11_60, // HANGUL JUNGSEONG FILLER
    0x28_00, // BRAILLE PATTERN BLANK
    0x31_64, // HANGUL FILLER
    0xff_a0, // HALFWIDTH HANGUL FILLER
]);
export function hasDisallowedChatCodePoints(body) {
    // A code-point scan rather than a regular expression. The equivalent pattern
    // needs control characters in its character class, which trips
    // `no-control-regex`; writing them as literals instead would put the exact
    // bytes this rule rejects into a source file, and a bidi override in source
    // misrenders every diff and editor that displays it.
    for (const character of body) {
        const codePoint = character.codePointAt(0) ?? 0;
        if (codePoint === LINE_FEED_CODE_POINT) {
            continue;
        }
        const isC0Control = codePoint < 0x00_20;
        const isDeleteOrC1 = codePoint >= 0x00_7f && codePoint <= 0x00_9f;
        const isBidiOverride = codePoint >= 0x20_2a && codePoint <= 0x20_2e;
        const isBidiIsolate = codePoint >= 0x20_66 && codePoint <= 0x20_69;
        // `for..of` yields whole code points, so a value left in the surrogate range
        // is necessarily unpaired.
        const isUnpairedSurrogate = codePoint >= 0xd8_00 && codePoint <= 0xdf_ff;
        if (isC0Control || isDeleteOrC1 || isBidiOverride || isBidiIsolate || isUnpairedSurrogate) {
            return true;
        }
    }
    return false;
}
/**
 * `PRD.md:398` requires that empty messages cannot be sent, and `.min(1)` alone
 * does not deliver that: a body of zero-width spaces, directional marks or
 * Hangul fillers has length but no visible content, and posts as a blank line.
 */
export function hasVisibleChatContent(body) {
    for (const character of body) {
        const codePoint = character.codePointAt(0) ?? 0;
        if (BLANK_BUT_NOT_WHITESPACE_CODE_POINTS.has(codePoint)) {
            continue;
        }
        // \p{Cf} covers the zero-width space, the LTR/RTL marks and the byte order
        // mark; \p{White_Space} covers spaces, tabs and line breaks.
        if (!/[\p{White_Space}\p{Cf}]/u.test(character)) {
            return true;
        }
    }
    return false;
}
export const chatBodySchema = z
    .string()
    .trim()
    .min(1)
    .max(CHAT_BODY_MAX_LENGTH)
    .refine((body) => !hasDisallowedChatCodePoints(body), {
    error: 'Chat text contains control or text-direction characters that are not allowed.',
})
    .refine(hasVisibleChatContent, {
    error: 'Chat text must contain at least one visible character.',
});
export const chatInputSchema = z.strictObject({
    version: z.literal(PROTOCOL_VERSION),
    clientMessageId: z.uuid(),
    body: chatBodySchema,
});
/**
 * The live-delivery half of `PRD.md:383-389`. Carries `messageId` so a client
 * that also sees the message arrive through the replicated history can render
 * each one exactly once.
 */
export const chatMessageSchema = z.strictObject({
    version: z.literal(PROTOCOL_VERSION),
    messageId: z.uuid(),
    /**
     * Room-scoped, strictly increasing, assigned by the server as it accepts each
     * message. This — not `sentAtMs` — is the ordering key. `Date.now()` has
     * millisecond resolution, so two messages in one tick would otherwise have to
     * be tie-broken on a random UUID, which is arbitrary and can invert a reply
     * and the message it answers.
     */
    sequence: z.int().nonnegative(),
    senderParticipantId: z.uuid(),
    senderDisplayName: z.string().min(1).max(64),
    body: chatBodySchema,
    sentAtMs: z.int().positive(),
});
/** Sent only to the author, so a pending local draft can be resolved. */
export const chatAcceptedSchema = z.strictObject({
    version: z.literal(PROTOCOL_VERSION),
    clientMessageId: z.uuid(),
    messageId: z.uuid(),
});
export const chatRejectionReasonSchema = z.enum(['rate-limited', 'invalid-body', 'not-in-room']);
/**
 * Sent only to the author. Without it a dropped message is indistinguishable
 * from a slow one, and the person retypes text the server already refused.
 */
export const chatRejectedSchema = z.strictObject({
    version: z.literal(PROTOCOL_VERSION),
    clientMessageId: z.uuid(),
    reason: chatRejectionReasonSchema,
    retryAfterMs: z.int().nonnegative().optional(),
});
export const roomMessageSchema = z.discriminatedUnion('type', [
    z.strictObject({ type: z.literal('movement'), payload: movementInputSchema }),
    z.strictObject({ type: z.literal('chat'), payload: chatInputSchema }),
]);
export const participantIdentityRequestSchema = z.strictObject({
    version: z.literal(PROTOCOL_VERSION),
});
export const participantIdentitySchema = z.strictObject({
    version: z.literal(PROTOCOL_VERSION),
    applicationParticipantId: z.uuid(),
});
export const mediaTokenResponseSchema = z.strictObject({
    version: z.literal(PROTOCOL_VERSION),
    applicationParticipantId: z.uuid(),
    liveKitRoomName: z.string().min(1),
    token: z.string().min(1),
});
export const mediaPermissionsSchema = z.strictObject({
    version: z.literal(PROTOCOL_VERSION),
    groupEpoch: z.int().nonnegative(),
    allowedParticipantIds: z.array(z.uuid()),
});
export const mediaReadySchema = z.strictObject({
    version: z.literal(PROTOCOL_VERSION),
});
export const mediaLeftSchema = z.strictObject({
    version: z.literal(PROTOCOL_VERSION),
});
export const mediaUnavailableSchema = z.strictObject({
    version: z.literal(PROTOCOL_VERSION),
    reason: z.enum(['concurrent-media-limit', 'daily-media-limit', 'speaking-policy']),
    retryAfterSeconds: z.int().positive().optional(),
});
export function parseRoomMessage(input) {
    return roomMessageSchema.parse(input);
}
export function assertNever(value) {
    throw new Error(`Unexpected value: ${JSON.stringify(value)}`);
}
export const browserCredentialClaimsSchema = z.strictObject({
    version: z.literal(PROTOCOL_VERSION),
    kind: z.literal('room-browser'),
    browserCredentialId: z.uuid(),
    expiresAt: z.int().positive(),
});
export const roomCapabilityClaimsSchema = z.strictObject({
    version: z.literal(PROTOCOL_VERSION),
    kind: z.literal('room-capability'),
    role: z.enum(['owner', 'admin']),
    capabilityId: z.uuid(),
    browserCredentialId: z.uuid(),
    roomSlug: roomSlugSchema,
    generation: z.int().nonnegative(),
    expiresAt: z.int().positive(),
});
export const roomAdminActionSchema = z.enum([
    'kick',
    'block-browser',
    'force-mute',
    'lock-room',
    'unlock-room',
    'promote',
    'demote',
    'approve-join',
    'reject-join',
    'set-who-can-speak',
    'set-approval-required',
    'rotate-private-code',
    'close-room',
]);
const targetedMemberCommand = (action) => z.strictObject({
    version: z.literal(PROTOCOL_VERSION),
    action: z.literal(action),
    targetParticipantId: z.uuid(),
});
export const roomAdminCommandSchema = z.discriminatedUnion('action', [
    targetedMemberCommand('kick'),
    targetedMemberCommand('block-browser'),
    targetedMemberCommand('promote'),
    z.strictObject({
        version: z.literal(PROTOCOL_VERSION),
        action: z.literal('force-mute'),
        targetParticipantId: z.uuid(),
        muted: z.boolean(),
    }),
    z.strictObject({
        version: z.literal(PROTOCOL_VERSION),
        action: z.literal('demote'),
        targetBrowserCredentialId: z.uuid(),
    }),
    z.strictObject({ version: z.literal(PROTOCOL_VERSION), action: z.literal('lock-room') }),
    z.strictObject({ version: z.literal(PROTOCOL_VERSION), action: z.literal('unlock-room') }),
    z.strictObject({
        version: z.literal(PROTOCOL_VERSION),
        action: z.literal('approve-join'),
        requestId: z.uuid(),
    }),
    z.strictObject({
        version: z.literal(PROTOCOL_VERSION),
        action: z.literal('reject-join'),
        requestId: z.uuid(),
    }),
    z.strictObject({
        version: z.literal(PROTOCOL_VERSION),
        action: z.literal('set-who-can-speak'),
        whoCanSpeak: whoCanSpeakSchema,
    }),
    z.strictObject({
        version: z.literal(PROTOCOL_VERSION),
        action: z.literal('set-approval-required'),
        approvalRequired: z.boolean(),
    }),
    z.strictObject({
        version: z.literal(PROTOCOL_VERSION),
        action: z.literal('rotate-private-code'),
    }),
    z.strictObject({ version: z.literal(PROTOCOL_VERSION), action: z.literal('close-room') }),
]);
export const roomAdminErrorCodeSchema = z.enum([
    'invalid-request',
    'unauthorized',
    'forbidden',
    'unknown-room',
    'room-closed',
    'unknown-participant',
    'unknown-request',
    'room-full',
    'rate-limited',
    'internal-error',
]);
export const roomAdminCommandResponseSchema = z.discriminatedUnion('ok', [
    z.strictObject({
        ok: z.literal(true),
        action: roomAdminActionSchema,
        privateAccessCode: privateAccessCodeSchema.optional(),
    }),
    z.strictObject({
        ok: z.literal(false),
        error: z.strictObject({
            code: roomAdminErrorCodeSchema,
            retryAfterSeconds: z.int().positive().optional(),
        }),
    }),
]);
export const joinRequestCreateSchema = z.strictObject({
    version: z.literal(PROTOCOL_VERSION),
    slug: roomSlugSchema,
    browserCredential: z.string().min(1).max(4_096),
});
export const joinRequestStatusSchema = z.enum(['pending', 'approved', 'rejected', 'expired']);
export const joinRequestCreateResponseSchema = z.strictObject({
    ok: z.literal(true),
    requestId: z.uuid(),
    requestSecret: z.string().min(32),
    expiresAt: z.int().positive(),
});
export const joinRequestPollResponseSchema = z.strictObject({
    ok: z.literal(true),
    status: joinRequestStatusSchema,
    token: z.string().min(1).optional(),
});
export const joinRequestSecretRequestSchema = z.strictObject({
    version: z.literal(PROTOCOL_VERSION),
    requestSecret: z.string().min(32).max(256),
});
export const pendingJoinRequestSummarySchema = z.strictObject({
    requestId: z.uuid(),
    browserCredentialId: z.uuid(),
    requestedAt: z.int().positive(),
    expiresAt: z.int().positive(),
});
export const roomAuthorityStateSchema = z.strictObject({
    version: z.literal(PROTOCOL_VERSION),
    role: roomRoleSchema,
    visibility: roomVisibilitySchema,
    locked: z.boolean(),
    approvalRequired: z.boolean(),
    whoCanSpeak: whoCanSpeakSchema,
    pendingJoinRequests: z.array(pendingJoinRequestSummarySchema),
    adminBrowserCredentialIds: z.array(z.uuid()),
});
export const roomCapabilityIssuedSchema = z.strictObject({
    version: z.literal(PROTOCOL_VERSION),
    role: z.literal('admin'),
    token: z.string().min(1).max(4_096),
});
/**
 * Operator control plane — `PRD.md:161-175`.
 *
 * Deliberately HTTP rather than a room message, for three reasons: the token
 * must be re-authorized on *every* call, not once at join; the kill switch has
 * to work when the operator is not in the room and the room may be at its cap
 * of five; and the invite endpoint already establishes the pattern.
 */
export const operatorTokenClaimsSchema = z.strictObject({
    version: z.literal(PROTOCOL_VERSION),
    /**
     * A distinct `kind` from `room-invite` is what stops a guest link from ever
     * being replayed as an operator credential. Both are HMAC-signed with the
     * same secret, so this discriminator is load-bearing, not decorative.
     */
    kind: z.literal('room-operator'),
    operatorId: z.string().min(1).max(64),
    roomSlug: roomSlugSchema,
    expiresAt: z.int().positive(),
});
export const operatorActionSchema = z.enum([
    'kick',
    'lock-room',
    'unlock-room',
    'force-mute',
    'media-kill-switch',
]);
export const operatorCommandSchema = z.discriminatedUnion('action', [
    z.strictObject({
        version: z.literal(PROTOCOL_VERSION),
        action: z.literal('kick'),
        targetParticipantId: z.uuid(),
    }),
    z.strictObject({ version: z.literal(PROTOCOL_VERSION), action: z.literal('lock-room') }),
    z.strictObject({ version: z.literal(PROTOCOL_VERSION), action: z.literal('unlock-room') }),
    z.strictObject({
        version: z.literal(PROTOCOL_VERSION),
        action: z.literal('force-mute'),
        targetParticipantId: z.uuid(),
    }),
    z.strictObject({
        version: z.literal(PROTOCOL_VERSION),
        action: z.literal('media-kill-switch'),
        /** Latched on; `false` releases it once the incident is over. */
        engaged: z.boolean(),
    }),
]);
export const operatorErrorCodeSchema = z.enum([
    'invalid-request',
    'unauthorized',
    'forbidden-slug',
    'unknown-slug',
    'unknown-participant',
    'rate-limited',
    'internal-error',
]);
export const operatorCommandResponseSchema = z.discriminatedUnion('ok', [
    z.strictObject({ ok: z.literal(true), action: operatorActionSchema }),
    z.strictObject({
        ok: z.literal(false),
        error: z.strictObject({
            code: operatorErrorCodeSchema,
            retryAfterSeconds: z.int().positive().optional(),
        }),
    }),
]);
